<template>
  <div class="project-page">

    <!-- Floating back button (rauno.me style) -->
    <button class="back-btn" @click="goBack">
      <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
        <path d="M8 1L3 6l5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
      </svg>
      Proyectos
    </button>

    <!-- Page content -->
    <div class="pp-body">

      <!-- Browser frame -->
      <div class="browser-frame">
        <!-- Browser top bar -->
        <div class="bf-bar">
          <div class="bf-dots">
            <span class="bf-dot bf-dot--red"></span>
            <span class="bf-dot bf-dot--yellow"></span>
            <span class="bf-dot bf-dot--green"></span>
          </div>
          <div class="bf-address">
            <span class="bf-lock">
              <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
                <rect x="1.5" y="4.5" width="7" height="4.5" rx="1" stroke="currentColor" stroke-width="1"/>
                <path d="M3 4.5V3a2 2 0 014 0v1.5" stroke="currentColor" stroke-width="1"/>
              </svg>
            </span>
            milo.design/{{ project ? project.slug : '' }}
          </div>
        </div>

        <!-- Frame content area -->
        <div class="bf-content" v-if="project">
          <!-- Project visual header -->
          <div class="pf-hero" :style="{ background: project.visualBg }">
            <div class="pf-hero-dots"></div>
            <div class="pf-hero-inner">
              <span class="pf-company">{{ project.company }}</span>
              <h1 class="pf-title">{{ project.title }}</h1>
              <div class="pf-tags">
                <span
                  v-for="tag in project.tags"
                  :key="tag"
                  class="pf-tag"
                  :style="{ color: project.accent }"
                >{{ tag }}</span>
              </div>
            </div>
          </div>

          <!-- Project details -->
          <div class="pf-details">
            <!-- Highlights grid -->
            <div class="pf-highlights">
              <div v-for="h in project.highlights" :key="h.label" class="pf-highlight">
                <span class="pf-h-label">{{ h.label }}</span>
                <span class="pf-h-value">{{ h.value }}</span>
              </div>
            </div>

            <div class="pf-divider"></div>

            <!-- Description -->
            <div class="pf-text-section">
              <h2 class="pf-section-title">Contexto del proyecto</h2>
              <p class="pf-long-desc">{{ project.longDesc }}</p>
            </div>

            <div class="pf-divider"></div>

            <!-- Stack -->
            <div class="pf-text-section">
              <h2 class="pf-section-title">Herramientas y stack</h2>
              <div class="pf-stack-chips">
                <span
                  v-for="tool in project.stack.split(' · ')"
                  :key="tool"
                  class="pf-stack-chip"
                >{{ tool }}</span>
              </div>
            </div>

            <div class="pf-divider"></div>

            <!-- Coming soon placeholder -->
            <div class="pf-wip">
              <div class="pf-wip-icon">
                <svg width="18" height="18" viewBox="0 0 18 18" fill="none">
                  <path d="M9 2v4M9 12v4M2 9h4M12 9h4M4.22 4.22l2.83 2.83M10.95 10.95l2.83 2.83M4.22 13.78l2.83-2.83M10.95 7.05l2.83-2.83" stroke="currentColor" stroke-width="1.3" stroke-linecap="round"/>
                </svg>
              </div>
              <p class="pf-wip-text">Case study completo próximamente — en construcción.</p>
            </div>
          </div>
        </div>

        <!-- Not found -->
        <div class="bf-content pf-notfound" v-else>
          <p>Proyecto no encontrado.</p>
        </div>
      </div>

      <!-- Next project -->
      <div class="pp-next" v-if="nextProject">
        <span class="pp-next-label">Siguiente proyecto</span>
        <button class="pp-next-btn" @click="$router.push('/project/' + nextProject.slug)">
          <span class="pp-next-company">{{ nextProject.company }}</span>
          <span class="pp-next-title">{{ nextProject.title }}</span>
          <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
            <path d="M1 7h12M8 2l5 5-5 5" stroke="currentColor" stroke-width="1.4" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
        </button>
      </div>

    </div>
  </div>
</template>

<script>
import { projects, getProject } from '@/data/projects.js';

export default {
  name: 'ProjectPage',

  computed: {
    project() {
      return getProject(this.$route.params.slug);
    },
    nextProject() {
      const idx = projects.findIndex(p => p.slug === this.$route.params.slug);
      return idx >= 0 && idx < projects.length - 1 ? projects[idx + 1] : projects[0];
    }
  },

  methods: {
    goBack() {
      if (window.history.length > 1) {
        this.$router.go(-1);
      } else {
        this.$router.push('/');
      }
    }
  },

  mounted() {
    document.body.style.backgroundColor = '#F7F6F2';
    document.body.style.padding = '0';
    window.scrollTo(0, 0);
  },

  beforeUnmount() {
    document.body.style.backgroundColor = '';
    document.body.style.padding = '';
  }
}
</script>

<style>
@import url('https://fonts.googleapis.com/css2?family=Geist:wght@300;400;500;600;700&family=Geist+Mono:wght@400;500&display=swap');
</style>

<style scoped>
*, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

.project-page {
  min-height: 100vh;
  background: var(--bg, #F7F6F2);
  font-family: 'Geist', 'Inter', ui-sans-serif, system-ui, sans-serif;
  -webkit-font-smoothing: antialiased;
}

/* ── FLOATING BACK BUTTON (rauno.me style) ── */
.back-btn {
  position: fixed;
  left: 40px;
  top: 48px;
  z-index: 50;
  display: flex;
  align-items: center;
  gap: 7px;
  font-family: inherit;
  font-size: 12.5px;
  font-weight: 500;
  color: var(--muted, #6B6B63);
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 6px 0;
  letter-spacing: -0.1px;
  transition: color .15s, transform .15s;
}
.back-btn:hover {
  color: var(--ink, #13130F);
  transform: translateX(-2px);
}
.back-btn svg { flex-shrink: 0; }

/* ── PAGE BODY ── */
.pp-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 120px 80px;
  gap: 40px;
  min-height: 100vh;
}

/* ── BROWSER FRAME ── */
.browser-frame {
  width: 100%;
  max-width: 860px;
  border-radius: 14px;
  border: 1px solid rgba(19,19,15,0.1);
  overflow: hidden;
  box-shadow:
    0 0 0 1px rgba(19,19,15,0.04),
    0 4px 6px -2px rgba(19,19,15,0.04),
    0 24px 48px -12px rgba(19,19,15,0.08);
  background: #fff;
}

/* Top bar */
.bf-bar {
  display: flex;
  align-items: center;
  gap: 16px;
  padding: 11px 16px;
  background: #F0EFEA;
  border-bottom: 1px solid rgba(19,19,15,0.08);
}
.bf-dots { display: flex; gap: 6px; align-items: center; }
.bf-dot {
  width: 11px; height: 11px; border-radius: 50%;
}
.bf-dot--red    { background: #FF5F57; }
.bf-dot--yellow { background: #FFBD2E; }
.bf-dot--green  { background: #28C840; }

.bf-address {
  flex: 1;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  font-family: 'Geist Mono', monospace;
  font-size: 11px;
  color: var(--muted, #6B6B63);
  background: rgba(255,255,255,.7);
  padding: 4px 14px;
  border-radius: 6px;
  max-width: 320px;
  margin: 0 auto;
  letter-spacing: -0.1px;
}
.bf-lock { color: var(--muted-2, #9A9A90); display: flex; align-items: center; }

/* Frame content */
.bf-content {
  background: #fff;
}

/* ── PROJECT VISUAL HEADER ── */
.pf-hero {
  position: relative;
  height: 280px;
  overflow: hidden;
  display: flex;
  align-items: flex-end;
}
.pf-hero-dots {
  position: absolute; inset: 0;
  background-image: radial-gradient(circle, rgba(0,0,0,0.07) 1px, transparent 1px);
  background-size: 24px 24px;
}
.pf-hero-inner {
  position: relative;
  padding: 28px 32px;
  display: flex;
  flex-direction: column;
  gap: 10px;
  width: 100%;
}
.pf-company {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; font-weight: 500;
  letter-spacing: .14em; text-transform: uppercase;
  color: var(--muted, #6B6B63);
}
.pf-title {
  font-size: clamp(22px, 3vw, 32px);
  font-weight: 500;
  letter-spacing: -0.03em;
  color: var(--ink, #13130F);
  line-height: 1.15;
}
.pf-tags { display: flex; gap: 8px; flex-wrap: wrap; }
.pf-tag {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; font-weight: 500;
  padding: 3px 9px; border-radius: 999px;
  background: rgba(255,255,255,.75);
  backdrop-filter: blur(4px);
  letter-spacing: .04em;
}

/* ── PROJECT DETAILS ── */
.pf-details {
  padding: 32px;
  display: flex;
  flex-direction: column;
  gap: 0;
}

.pf-highlights {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0;
  margin-bottom: 28px;
}
.pf-highlight {
  display: flex;
  flex-direction: column;
  gap: 5px;
  padding-right: 24px;
  border-right: 1px solid rgba(19,19,15,0.06);
  padding-left: 0;
}
.pf-highlight:first-child { padding-left: 0; }
.pf-highlight:last-child { border-right: none; padding-left: 24px; }
.pf-highlight:not(:first-child):not(:last-child) { padding-left: 24px; }

.pf-h-label {
  font-family: 'Geist Mono', monospace;
  font-size: 9.5px; font-weight: 500;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--muted-2, #9A9A90);
  display: block;
}
.pf-h-value {
  font-size: 13px; font-weight: 500;
  color: var(--ink, #13130F);
  letter-spacing: -0.01em;
  display: block;
}

.pf-divider {
  height: 1px;
  background: rgba(19,19,15,0.07);
  margin: 28px 0;
}

.pf-text-section { display: flex; flex-direction: column; gap: 14px; }
.pf-section-title {
  font-size: 11px; font-weight: 500;
  font-family: 'Geist Mono', monospace;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--muted-2, #9A9A90);
}
.pf-long-desc {
  font-size: 14.5px;
  line-height: 1.7;
  color: var(--muted, #6B6B63);
  max-width: 620px;
}

.pf-stack-chips { display: flex; flex-wrap: wrap; gap: 8px; }
.pf-stack-chip {
  font-family: 'Geist Mono', monospace;
  font-size: 11px; font-weight: 500;
  padding: 5px 12px; border-radius: 6px;
  background: rgba(19,19,15,0.04);
  color: var(--ink-2, #2A2A24);
  border: 1px solid rgba(19,19,15,0.08);
}

/* WIP placeholder */
.pf-wip {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px 18px;
  border-radius: 10px;
  border: 1px dashed rgba(19,19,15,0.12);
  background: rgba(19,19,15,0.02);
}
.pf-wip-icon { color: var(--muted-2, #9A9A90); display: flex; align-items: center; }
.pf-wip-text {
  font-size: 12.5px;
  color: var(--muted-2, #9A9A90);
  letter-spacing: -0.1px;
}

/* Not found */
.pf-notfound {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px;
  color: var(--muted, #6B6B63);
  font-size: 14px;
}

/* ── NEXT PROJECT ── */
.pp-next {
  width: 100%;
  max-width: 860px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 20px 0;
  border-top: 1px solid rgba(19,19,15,0.08);
}
.pp-next-label {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; font-weight: 500;
  letter-spacing: .1em; text-transform: uppercase;
  color: var(--muted-2, #9A9A90);
}
.pp-next-btn {
  display: flex;
  align-items: center;
  gap: 10px;
  background: none;
  border: none;
  cursor: pointer;
  font-family: inherit;
  padding: 8px 14px;
  border-radius: 8px;
  border: 1px solid rgba(19,19,15,0.1);
  background: #fff;
  transition: all .15s;
}
.pp-next-btn:hover {
  background: var(--ink, #13130F);
  border-color: var(--ink, #13130F);
  color: #fff;
}
.pp-next-btn:hover .pp-next-company,
.pp-next-btn:hover .pp-next-title { color: rgba(255,255,255,.7); }
.pp-next-company {
  font-family: 'Geist Mono', monospace;
  font-size: 10px; color: var(--muted-2, #9A9A90);
  letter-spacing: .06em; text-transform: uppercase;
}
.pp-next-title {
  font-size: 13px; font-weight: 500;
  color: var(--ink, #13130F);
  letter-spacing: -0.01em;
}
.pp-next-btn svg { transition: transform .15s; }
.pp-next-btn:hover svg { transform: translateX(2px); }

/* Responsive */
@media (max-width: 900px) {
  .back-btn {
    position: fixed;
    left: 16px;
    top: 20px;
    transform: none;
    writing-mode: horizontal-tb;
  }
  .back-btn:hover { transform: translateX(-2px); }
  .pp-body { padding: 64px 16px 60px; }
  .pf-highlights { grid-template-columns: repeat(2, 1fr); gap: 16px; }
  .pf-highlight { border-right: none !important; padding-left: 0 !important; padding-right: 0 !important; }
  .pf-details { padding: 20px; }
}
</style>
